import { getCustomTag, sendNotification, useTranslation } from '@msdk/common'
import {
  CustomActionButtonAspect,
  DefaultActionsDetails,
  DefaultActionType,
  EntityOperation,
  ServerOperationResult,
} from '@msdk/definitions'
import { ButtonIcon, ButtonPrimary, COLORS, IconSelector, styled, Tooltip } from '@msdk/ui'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { useComponentModal, useFormModal } from '../../hooks'
import { messagesFromEntityOperation } from '../../utils'
import { ActionConfirmationModal } from '../ActionConfirmationModal'
import { ActionLabels, getDeleteActionLabels } from '../DynamicView/RowActions/labels-utils'
import { ActionVisualizationMode } from './action-visualization'
import { ActionMenuItem, ActionMenuText } from './ActionMenuItem'

const DeleteButtonStyled = styled(ButtonPrimary)`
  background-color: ${COLORS.error.main};
  color: white;

  &:hover {
    background-color: ${COLORS.error.dark};
  }

  margin-right: 5px;
`
const StyledButtonIcon = styled(ButtonIcon)`
  padding: 4px;
  color: ${({ theme }) => theme.palette.primary.main};
`
interface Props {
  entityName: string
  formName: string
  id: string
  visualizationMode: ActionVisualizationMode
  onDelete: () => Promise<EntityOperation>
  onClose?: () => void
  actionDetails?: DefaultActionsDetails
  type?: DefaultActionType | undefined
}

export function DeleteAction(props: Props) {
  const t = useTranslation()
  const { createModal } = useComponentModal()
  const { getModal } = useFormModal()
  const navigate = useNavigate()

  const labels: ActionLabels = getDeleteActionLabels(props.actionDetails, t)

  const handleClick = async () => {
    props.onClose && props.onClose()
    const onConfirm = async () => {
      try {
        const res = await props.onDelete()

        const serverMessages = messagesFromEntityOperation(res)
        if (serverMessages.length) {
          const translated = serverMessages.map((msg) => `${t(msg.idml, msg.description)}`).join('\n')
          sendNotification({ message: translated, status: 'error' })
        } else {
          if (res.result === ServerOperationResult.Success) {
            sendNotification({ message: labels.success, status: 'success' })

            const mRef = getModal(props.entityName, props.formName, props.id)
            if (mRef) {
              mRef.close()
            } else {
              navigate(-1)
            }
          } else {
            sendNotification({ message: labels.error, status: 'error' })
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          const regex = /\{[^]*\}/
          const match = error.message.match(regex)

          if (match) {
            const jsonString = match[0]
            const errorObject = JSON.parse(jsonString)
            if (errorObject.status == '409') {
              sendNotification({ message: errorObject.response, status: 'error' })
              return
            }
          }
        }
        sendNotification({ message: labels.error, status: 'error' })
      }
    }

    const modalRef = createModal(
      <ActionConfirmationModal
        title={labels.title}
        body={labels.body}
        onConfirm={onConfirm}
        confirmationLabel={labels.confirmationLabel}
        type={DefaultActionType.Delete}
      />,
      () => {
        //do nothing
      },
    )

    modalRef.open()
  }

  return (
    <>
      {props.visualizationMode === 'SingleAction' && (
        <Tooltip title={labels.description} arrow>
          <StyledButtonIcon onClick={handleClick}>
            {labels.iconTag && <IconSelector iconName={labels.iconTag} />}
            {!labels.iconTag && labels.description}
          </StyledButtonIcon>
        </Tooltip>
      )}
      {props.visualizationMode === 'Buttons' && (
        <DeleteButtonStyled
          onClick={handleClick}
          key="DELETE"
          data-test-id={getCustomTag(['modal', 'DELETE', 'Delete_Permanently', 'button'])}
        >
          {labels.description}
        </DeleteButtonStyled>
      )}
      {props.visualizationMode === 'MenuItems' && (
        <ActionMenuItem
          aspect={CustomActionButtonAspect.Alternative}
          onClick={handleClick}
          data-test-id={getCustomTag(['modal', 'DELETE', 'Delete_Permanently', 'button'])}
        >
          <IconSelector iconName={labels.iconTag} />
          <ActionMenuText>{labels.description}</ActionMenuText>
        </ActionMenuItem>
      )}
    </>
  )
}
